import { ApiAccessControlList } from "@/types/approvals/approval.types.ts";
import { ApiComment } from "@/types/comment.types.ts";
import { ApiFile, ApiReaction } from "@/types/common.types.ts";
import { ApiEmployee, ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { ApiFilterBase } from "@skailer-com/uikit/core";
import { ApiApplicantMentionState } from "@/types/recruit/applicant.types.ts";
import { Rule, RuleGroup, RuleGroupShort } from "@/types/rule.types.ts";
import { createContext, Dispatch, SetStateAction, useContext } from "react";

export interface ApiBlogCommentPayload {
  id: number;
  created_by: ApiEmployeeShort;
  datetime_created_at: Date;
  text: string;
}

export interface ApiBlogPostShort {
  id: number;
  title: string;
  caption: string;
  body?: string;
  reactions: ApiReaction[];
  author?: ApiEmployeeShort;
  datetime_created_at: Date;
  datetime_published_at?: Date;
  channels: ApiBlogChannelShort[];
  comment_data?: ApiComment[];
  hero_image_file?: ApiBlogPostImage | null;
  files?: ApiFile[];
  total_comments: number;
  is_read: boolean;
  color?: string;
  acl?: ApiAccessControlList;
  notification_settings: BlogPostNotificationSettings;
  reactions_disabled: boolean;
  comments_disabled: boolean;
  mentioned_employees?: ApiEmployeeShort[];
  views_count: number;
}

export interface ApiBlogPost extends ApiBlogPostShort {
  body: string;
  files: ApiFile[];
}

const BLOG_PREVIEWABLE_ATTACHMENT_MIME_TYPES = new Set(["application/pdf"]);

export function isBlogAttachmentPreviewable(file: ApiFile) {
  const mimeType = file.mime_type.toLowerCase();
  return (
    BLOG_PREVIEWABLE_ATTACHMENT_MIME_TYPES.has(mimeType) ||
    mimeType.startsWith("image/") ||
    mimeType.startsWith("video/") ||
    mimeType.startsWith("audio/")
  );
}

export interface ApiBlogPostImage {
  color_palette: string[];
  file_uuid: string;
  url: string;
}

export const ApiBlogPostMock: ApiBlogPost = {
  id: 0,
  title: "",
  caption: "",
  reactions: [],
  datetime_created_at: new Date(),
  channels: [],
  body: "",
  files: [],
  total_comments: 0,
  is_read: true,
  comments_disabled: false,
  reactions_disabled: false,
  views_count: 0,
  notification_settings: {
    in_app: true,
    slack: false,
    email: false,
  },
};

export interface ApiBlogPostSave {
  title: string;
  caption: string;
  body: string;
  file_uuids?: string[];
  channels: number[];
  disable_comments: boolean;
  disable_reactions: boolean;
  notification_settings: BlogPostNotificationSettings;
  color: string | undefined | null;
  hero_image_file_uuid: string | undefined | null;
  schedule_settings: ApiBlogPostScheduleSettings | null;
}

export interface ApiBlogPostScheduleSettings {
  datetime_publish_at: string;
  timezone: string;
}

export interface ApiBlogChannelShort {
  id: number;
  emoji: string;
  title: string;
  description: string;
  is_subscribed: boolean;
  has_unread: boolean;
  is_subscribe_all_employees?: boolean;
  slack_channel_id?: string;
}

export interface ApiBlogChannel extends ApiBlogChannelShort {
  subscribes_info: {
    employees: ApiEmployeeShort[];
    count: number;
  };
  publish_apply_to_groups?: RuleGroup[] | null;
  publish_apply_to?: Rule[] | null;
  is_subscribe_all_employees: boolean;
  view_apply_to_groups?: RuleGroup[] | null;
  view_apply_to?: Rule[] | null;
  acl?: ApiAccessControlList;
}

export interface ApiBlogChannelSave {
  emoji: string;
  title: string;
  description: string;
  is_subscribe_all_employees: boolean;
  view_apply_to_groups: RuleGroupShort[];
  publish_apply_to_groups: RuleGroupShort[];
  slack_channel_id?: string | null;
}

export function getBlogChannelRuleGroups(
  channel: ApiBlogChannel | undefined,
  field: "view_apply_to" | "publish_apply_to"
): RuleGroup[] {
  const groups = channel?.[`${field}_groups`];
  if (groups != null) return groups;
  const rules = channel?.[field] ?? [];
  return rules.length > 0 ? [{ rules }] : [];
}

export interface ApiBlogPostsSearchFilter extends ApiFilterBase {
  channel_ids?: number[];
  mention_state?: ApiApplicantMentionState | undefined;
}

export interface ApiBlogCommentsSearchFilter extends ApiFilterBase {
  post_id: number;
}

export interface ApiBlogReactionsSearchFilter {
  post_id: number;
  emoji: string;
}

export interface ApiBlogPostCommentsSearchFilter extends ApiFilterBase {
  post_id: number;
}

export interface ApiBlogCommentSave {
  text: string;
  file_uuids?: string[];
}

export enum BlogPostMode {
  short = "short",
  long = "long",
  preview = "preview",
}

export type BlogPostModeType = keyof typeof BlogPostMode;

export enum ImageFileTypes {
  PNG = "image/png",
  JPEG = "image/jpeg",
  JPG = "image/jpg",
  GIF = "image/gif",
  SVG = "image/svg+xml",
  WEBP = "image/webp",

  TIFF = "image/tiff",
  TIF = "image/tif",
  BMP = "image/bmp",
  ICO = "image/x-icon",
  HEIF = "image/heif",
  HEIC = "image/heic",
  AVIF = "image/avif",
}

export const WebpUploadableImageFileTypes: Record<string, string> = Object.fromEntries(
  Object.entries(ImageFileTypes).filter(([key]) => key !== "SVG")
);

export enum BlogNotifyType {
  in_app = "in_app",
  email = "email",
  slack = "slack",
}

export type BlogPostNotificationSettings = {
  [key in BlogNotifyType]: boolean;
};

export enum BlogEditSteps {
  form = "form",
  preview = "preview",
  settings = "settings",
}

type BlogEditContextType = {
  step: BlogEditSteps;
  postId: string | undefined;
  post: ApiBlogPost | undefined;
  withoutImage: boolean;
  setStep: (step: BlogEditSteps) => void;
  setPost: Dispatch<SetStateAction<ApiBlogPost | undefined>>;
  registerTemporaryFile: (fileUuid: string) => void;
  unregisterTemporaryFile: (fileUuid: string) => void;
  isTemporaryFile: (fileUuid: string) => boolean;
  clearAttachmentTracking: () => void;
  setWithoutImage: (without: boolean) => void;
};

export const BlogEditContext = createContext<BlogEditContextType | undefined>(undefined);

export const useBlogEditContext = () => {
  const context = useContext(BlogEditContext);
  if (!context) {
    throw new Error("useBlogEditContext must be used within a BlogEditContextProvider");
  }
  return context;
};

export interface ApiBlogChannelFilter {
  can: ApiBlogChannelCan;
}

export enum ApiBlogChannelCan {
  view = "view",
  publish = "publish",
}

export interface ApiBlogMyChannelsUnreadCount {
  unread_post_count: number;
  unread_channel_ids: number[];
}

export type ApiBlogPostViewer = ApiEmployee & { datetime_viewed_at: Date };

export interface ApiBlogPostViewers {
  viewed_employees: ApiBlogPostViewer[];
}

export interface ApiBlogPostViewersFilter extends ApiFilterBase {
  post_id: number;
}
